import { cva } from 'class-variance-authority'

import { ToggleItemWidth, ToggleSize } from './types'

/**
 * Container variants for the ToggleGroup wrapper.
 *
 * - `default`: a segmented-pill wrapper with a shared background; columns are
 *   equal-width or sized to content per `itemWidth`.
 * - `chip`: independent chips with no shared background and a 4px gap.
 */
export const toggleGroupVariants = cva('', {
  defaultVariants: {
    variant: 'default',
    itemWidth: ToggleItemWidth.Equal,
  },
  variants: {
    variant: {
      default: [
        'inline-grid grid-flow-col isolate',
        'bg-surface-controls-secondary',
        'rounded-md',
        'gap-0.75',
      ],
      chip: ['inline-flex', 'gap-1'],
    },
    itemWidth: {
      [ToggleItemWidth.Equal]: '',
      [ToggleItemWidth.Content]: '',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      itemWidth: ToggleItemWidth.Equal,
      className: 'auto-cols-fr',
    },
    {
      variant: 'default',
      itemWidth: ToggleItemWidth.Content,
      className: 'auto-cols-auto',
    },
  ],
})

/**
 * Item variants for individual toggle buttons. The chip variant auto-widths to
 * its content (20px min); its height comes from the shared `size` scale.
 */
export const toggleVariants = cva(
  [
    // Layout & interaction - shared across variants
    'flex items-center justify-center',
    'cursor-pointer',
    'text-text-secondary',
    // Transition: `background` and `border-color` animate; `border-style`
    // (dashed -> solid on the chip variant) is not animatable and snaps.
    '[--toggle-duration-bg:25ms]',
    '[--toggle-duration-border:25ms]',
    'data-[pressed]:[--toggle-duration-bg:175ms]',
    'data-[pressed]:[--toggle-duration-border:175ms]',
    '[transition:background_var(--toggle-duration-bg)_ease,box-shadow_175ms_ease,color_175ms_ease,border-color_var(--toggle-duration-border)_ease]',
    // Hover / pressed text
    'hover:not-data-[disabled]:text-text-primary',
    'data-[pressed]:text-text-primary',
    // Focus
    'focus-visible:outline-none',
    'focus-visible:not-data-[disabled]:text-text-primary',
    // Disabled
    'data-[disabled]:opacity-disabled',
    'data-[disabled]:cursor-not-allowed',
  ],
  {
    defaultVariants: {
      variant: 'default',
      size: ToggleSize.Medium,
      inGroup: true,
    },
    variants: {
      variant: {
        default: [
          'rounded-md',
          'min-w-0 whitespace-nowrap',
          'bg-transparent border border-transparent',
          // Selected (pressed)
          'data-[pressed]:bg-surface-controls-default',
          'data-[pressed]:shadow-xs',
          '[&:where([data-pressed])]:border-border-controls-default',
          'data-[pressed]:z-10',
          // Focus
          'focus-visible:not-data-[disabled]:z-20',
          'focus-visible:not-data-[disabled]:not-data-[pressed]:shadow-focus-ring-subtle',
          'focus-visible:not-data-[disabled]:not-data-[pressed]:border-border-default',
          'dark:focus-visible:not-data-[disabled]:not-data-[pressed]:border-border-secondary',
          'focus-visible:not-data-[disabled]:data-[pressed]:[box-shadow:var(--shadow-xs),var(--shadow-focus-ring)]',
          'focus-visible:not-data-[disabled]:data-[pressed]:border-border-focused',
        ],
        chip: [
          // Auto-width, 20px min; height/padding from the size compounds
          'min-w-5 rounded-sm border',
          'text-size-sm leading-sm',
          // Not pressed: dashed border, transparent
          'border-dashed border-border-controls-default bg-transparent',
          // Pressed: subtle teal tint + teal border (mirrors the choice component)
          'data-[pressed]:border-solid data-[pressed]:border-teal-50',
          'data-[pressed]:bg-teal-10',
          // Focus: keyboard ring only (no drop shadow)
          'focus-visible:not-data-[disabled]:z-20',
          'focus-visible:not-data-[disabled]:not-data-[pressed]:shadow-focus-ring-subtle',
          'focus-visible:not-data-[disabled]:not-data-[pressed]:border-border-default',
          'dark:focus-visible:not-data-[disabled]:not-data-[pressed]:border-border-secondary',
          'focus-visible:not-data-[disabled]:data-[pressed]:[box-shadow:var(--shadow-focus-ring)]',
          'focus-visible:not-data-[disabled]:data-[pressed]:border-border-focused',
        ],
      },
      size: {
        [ToggleSize.XSmall]: '',
        [ToggleSize.Small]: '',
        [ToggleSize.Medium]: '',
      },
      inGroup: {
        false: '',
        true: '',
      },
    },
    compoundVariants: [
      // Chip height + padding per size (width is auto)
      {
        variant: 'chip',
        size: ToggleSize.XSmall,
        className: 'h-5 px-1',
      },
      {
        variant: 'chip',
        size: ToggleSize.Small,
        className: 'h-6 px-1.5',
      },
      {
        variant: 'chip',
        size: ToggleSize.Medium,
        className: 'h-7 px-2',
      },
      // Size and standalone (`inGroup: false`) styling below is default-only
      {
        variant: 'default',
        size: ToggleSize.Small,
        className: 'h-6 px-2 text-size-sm leading-sm',
      },
      {
        variant: 'default',
        size: ToggleSize.Medium,
        className: 'h-7 px-2 text-size-md leading-md',
      },
      {
        variant: 'default',
        inGroup: false,
        className: 'bg-surface-secondary [&:has(>svg:only-child)]:px-0',
      },
      // Icon-only items in a group are square too; a min width still lets an
      // equal-width group stretch them.
      {
        variant: 'default',
        size: ToggleSize.Small,
        inGroup: true,
        className:
          '[&:has(>svg:only-child)]:min-w-6 [&:has(>svg:only-child)]:px-0',
      },
      {
        variant: 'default',
        size: ToggleSize.Medium,
        inGroup: true,
        className:
          '[&:has(>svg:only-child)]:min-w-7 [&:has(>svg:only-child)]:px-0',
      },
      {
        variant: 'default',
        size: ToggleSize.Small,
        inGroup: false,
        className: '[&:has(>svg:only-child)]:w-6',
      },
      {
        variant: 'default',
        size: ToggleSize.Medium,
        inGroup: false,
        className: '[&:has(>svg:only-child)]:w-7',
      },
    ],
  },
)
